import * as d3 from 'd3-selection'
import { range } from 'd3-array'
import * as scale from 'd3-scale'
import current from '../current-day.js'

function diamond (selection, xScale, yScale, height) {
  var width = yScale.bandwidth()
    , milestone = selection.selectAll('path.gantt-mini-milestone')
                           .data(d => {
                             if (!d.node.endDate) {
                               // Don't draw the milestone if it doesn't have an end date
                               return []
                             }
                             return [d]
                           })

  milestone.exit().remove()

  milestone.enter()
           .append('path')
           .merge(milestone)
             .attr('class', d => `gantt-mini-milestone ${d.node.state} ${d.node.state}-${d.node.scheduleStatus}`)
             .attr('transform', d => `translate(${-width / 2})`)
             .attr('d', `M ${width / 2},0 L${width},${height / 2} L ${width / 2},${height} L 0,${height / 2} Z`)
}

function rect (selection, xScale, yScale, height) {
  var task = selection.selectAll('path.gantt-mini-task')
                      .data(d => {
                        if (!d.node.startDate || !d.node.endDate) {
                          // Can't draw
                          return []
                        }

                        if (d.node.percentComplete > 0 && d.node.percentComplete < 1) {
                          // Return two paths to indicate percentage
                          var width = (xScale(d.node.endDate) - xScale(d.node.startDate)) || 0 // full width
                          return [{
                            state: 'gantt-item-uncomplete',
                            scheduleStatus: '',
                            width: width
                          }, {
                            state: d.node.state,
                            scheduleStatus: d.node.scheduleStatus,
                            width: width * d.node.percentComplete
                          }]
                        } else {
                         return [{
                           state: d.node.state,
                           scheduleStatus: d.node.scheduleStatus,
                           width: (xScale(d.node.endDate) - xScale(d.node.startDate)) || 0
                         }]
                        }
                      })

  task.exit().remove()

  task.enter()
      .append('path')
      .merge(task)
      .attr('class', d => `gantt-mini-task ${d.state} ${d.state}-${d.scheduleStatus}`)
      .attr('d', d => {
        return `M 0,0 L${d.width},0 L ${d.width},${height} L 0,${height} Z`
      })
}

var shapes = {
  milestone: diamond,
  task: rect,
  initiative: rect
}

function shape (context, xScale, yScale, height) {
  var selection = context.selection ? context.selection() : context

  selection.each(function (d) {
    return d3.select(this)
             .call(shapes[d.node.type], xScale, yScale, height)
  })
}

/*
 * Draws the mini version of the chart
 *
 * `xScale` contains the scaler for the entire data set (including nodes collapsed in the tree)
 *  so min/max of the full mini chart, but fits inside the footer dimensions
 */
export default function (context, dimensions, xScale, today) {
  var selection = context.selection ? context.selection() : context

  selection.each(function (data) {
    if (!data) {
      // The initiative data hasn't been bound, yet. This is probably
      // Some race condition or improper API calls. The data is bound
      // when the tree `rebind` fires. This should fix itself.
      return
    }
    var margin = dimensions.footer.margin
      , width = Math.max(0, dimensions.right.width - margin.left - margin.right)
      , height = Math.max(0, dimensions.footer.height - margin.top - margin.bottom)
      , yScale = scale.scaleBand()
                      .domain(range(0, data.length))
                      .range([0, height])
                      .paddingOuter(.1)
                      .paddingInner(.1)
      , group = d3.select(this)
                  .selectAll('g.gantt-mini-items')
                  .data(function (d) { return [d] })
                  .enter()
                  .insert('g', ':first-child')
                    .attr('class', 'gantt-mini-items')
                    .merge(d3.select(this).selectAll('g.gantt-mini-items'))
                    .call(current, xScale, height, today)

    // Draw all the items in the mini chart
    var _items = group.selectAll('.item')
                      .data(d => {
                        return d.map((_d, i) => {
                          return {
                            y: yScale(i),
                            x: (_d.type == 'milestone' ? xScale(_d.endDate)
                                                       : xScale(_d.startDate)) || 0,
                            node: _d
                          }
                        })
                      }, d => d.node.id)

    _items.exit()
          .transition(context)
          .call(shape, xScale, yScale, 0)
          .style('opacity', 0)
          .remove()

    _items.enter()
          .append('g')
            .attr('class', 'item')
            .call(shape, xScale, yScale, 0)
            .style('opacity', 0)
            .attr('transform', function (d, i) {
              var y = (yScale(i - 1) || 0) + yScale.bandwidth()
              return `translate(${d.x},${y})`
            })
            .merge(_items)
            .transition(context)
            .style('opacity', 1)
            .call(shape, xScale, yScale, yScale.bandwidth())
            .attr('transform', d => `translate(${d.x},${d.y})`)
  })
}
